import React, { useCallback, useEffect, useState } from 'react'
import IwmRootMappingDialog from './IwmRootMappingDialog'
import {
  fetchBugTypesByPass,
  fetchRootBugTypeAggregate,
  type FetchRootBugTypeAggregateResult
} from '../../../utils/weldTelemetryClient'
import {
  buildIwmRootMappingPreview,
  mergeRootBugTypeRows,
  type IwmRootMappingPreview
} from '../../../utils/pqr/iwmRootMappingPreview'
import { emptyRootBugTypeAggregate } from '../../../utils/pqr/iwmRootAggregateDisplay'
import { IWM_ROOT_MAPPING_BUTTON_LABEL } from '../../../utils/pqr/swapIwmBugType'

interface Props {
  enabled: boolean
  busy?: boolean
  projectId?: string
  pipeSetId?: string
  weldId?: string
  alxPassNames?: string[]
  preheatUnit?: string
  /** When false, no toolbar button (parent opens via `open` / `onOpenChange`). */
  showButton?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onChange: (next: boolean) => void | Promise<void>
}

/**
 * IWM Root mapping dialog (+ optional trigger button). Saves meta.swapIwmBugType.
 */
export default function SwapIwmBugTypeToggle({
  enabled,
  busy = false,
  projectId,
  pipeSetId,
  weldId,
  alxPassNames = [],
  preheatUnit,
  showButton = true,
  open: openProp,
  onOpenChange,
  onChange
}: Props) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const controlled = openProp !== undefined
  const open = controlled ? openProp : uncontrolledOpen

  const setOpen = useCallback(
    (next: boolean) => {
      if (!controlled) setUncontrolledOpen(next)
      onOpenChange?.(next)
    },
    [controlled, onOpenChange]
  )

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [preview, setPreview] = useState<IwmRootMappingPreview | null>(null)
  const [aggregate, setAggregate] = useState<FetchRootBugTypeAggregateResult | null>(null)

  const loadPreview = useCallback(async () => {
    setLoading(true)
    setPreview(null)
    setAggregate(null)
    try {
      let telemetryPasses: Array<{ pass: string; bugTypes: Array<'CW' | 'CCW'> }> = []
      let agg: FetchRootBugTypeAggregateResult = emptyRootBugTypeAggregate()
      if (projectId && pipeSetId && weldId) {
        try {
          const bugResult = await fetchBugTypesByPass({ projectId, pipeSetId, weldId })
          telemetryPasses = bugResult.passes
        } catch {
          // Fall back to ALX pass names only.
        }
        try {
          agg = await fetchRootBugTypeAggregate({ projectId, pipeSetId, weldId })
        } catch {
          // Keep empty aggregate when telemetry is missing.
        }
      }
      const rows = mergeRootBugTypeRows(telemetryPasses, alxPassNames)
      setPreview(buildIwmRootMappingPreview(rows))
      setAggregate(agg)
    } finally {
      setLoading(false)
    }
  }, [projectId, pipeSetId, weldId, alxPassNames])

  useEffect(() => {
    if (open) void loadPreview()
  }, [open, loadPreview])

  const handleOpenClick = useCallback(() => {
    if (busy || saving) return
    setOpen(true)
  }, [busy, saving, setOpen])

  const handleSave = useCallback(
    async (flipEnabled: boolean) => {
      setSaving(true)
      try {
        await onChange(flipEnabled)
        setOpen(false)
      } finally {
        setSaving(false)
      }
    },
    [onChange, setOpen]
  )

  return (
    <>
      {showButton ? (
        <button
          type="button"
          data-testid="wp-swap-iwm-switch"
          disabled={busy || saving}
          onClick={handleOpenClick}
          className="inline-flex items-center gap-1.5 rounded-md border border-[#E2E5EA] bg-white px-3 py-1.5 text-xs font-semibold text-heritage-grey shadow-sm transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {IWM_ROOT_MAPPING_BUTTON_LABEL}
        </button>
      ) : null}

      <IwmRootMappingDialog
        open={open}
        flipEnabled={enabled}
        preview={preview}
        aggregate={aggregate}
        loading={loading}
        saving={saving}
        preheatUnit={preheatUnit}
        onSave={handleSave}
        onCancel={() => (saving ? undefined : setOpen(false))}
      />
    </>
  )
}
